/**
 * RFI Store Slice
 *
 * Zustand slice for RFI lifecycle state management.
 * Domain: ServicesContext > RFIAggregate
 *
 * Manages RFI list state per actor view (buyer/org/consultant)
 * and the current RFI detail. All lifecycle transitions update
 * currentRFI in state when the rfiId matches.
 */

import {
  clearError as clearOpError,
  isOperationLoading,
  setError,
  setLoaded,
  setLoading,
} from '@gw/ui';

import { rfisApi } from '@/api/rfis';
import type { RFI, RFIStatus, SubmitRFIRequest } from '@/types/rfi.types';

import type { ErrorStates, LoadedTimestamps, LoadingStates } from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// ============================================================================
// Slice Interface
// ============================================================================

export interface RFISlice {
  // Current RFI detail
  currentRFI: RFI | null;
  // List state per view
  buyerRFIs: RFI[];
  buyerRFIsNextCursor: string | null;
  orgRFIs: RFI[];
  orgRFIsNextCursor: string | null;
  consultantRFIs: RFI[];
  consultantRFIsNextCursor: string | null;
  // Loading/error
  rfiIsLoading: LoadingStates;
  rfiHasLoaded: LoadingStates;
  rfiLastLoaded: LoadedTimestamps;
  rfiErrors: ErrorStates;
  // Actions
  fetchBuyerRFIs: (params?: {
    cursor?: string;
    status?: RFIStatus;
    limit?: number;
  }) => Promise<void>;
  // GW-10607: cursor-pagination "load more" for the buyer RFI list —
  // appends the next page (keyed off `buyerRFIsNextCursor`) instead of
  // replacing `buyerRFIs` the way `fetchBuyerRFIs` does.
  loadMoreBuyerRFIs: () => Promise<void>;
  fetchOrgRFIs: (
    orgId: string,
    params?: { cursor?: string; status?: RFIStatus; limit?: number }
  ) => Promise<void>;
  fetchConsultantRFIs: (params?: {
    cursor?: string;
    status?: RFIStatus;
    limit?: number;
  }) => Promise<void>;
  // GW-10607: cursor-pagination "load more" for the consultant (assigned)
  // RFI list — appends the next page instead of replacing `consultantRFIs`.
  loadMoreConsultantRFIs: () => Promise<void>;
  fetchRFI: (rfiId: string) => Promise<void>;
  submitRFI: (data: SubmitRFIRequest) => Promise<RFI>;
  assignConsultant: (rfiId: string, consultantUserId: string) => Promise<void>;
  cancelRFI: (rfiId: string) => Promise<void>;
  deliverRFI: (rfiId: string, deliverableReference: string) => Promise<void>;
  acceptRFI: (rfiId: string) => Promise<void>;
  disputeRFI: (rfiId: string, reason: string) => Promise<void>;
  resolveDispute: (
    rfiId: string,
    resolution: 'completed' | 'refunded',
    notes?: string
  ) => Promise<void>;
  extendLTIOV: (rfiId: string, proposedLtiov: string, reason: string) => Promise<void>;
  approveExtension: (rfiId: string) => Promise<void>;
  rejectExtension: (rfiId: string) => Promise<void>;
  setCurrentRFI: (rfi: RFI | null) => void;
  clearRFIError: (operation?: string) => void;
  resetRFI: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialRFIState = {
  currentRFI: null,
  buyerRFIs: [],
  buyerRFIsNextCursor: null,
  orgRFIs: [],
  orgRFIsNextCursor: null,
  consultantRFIs: [],
  consultantRFIsNextCursor: null,
  rfiIsLoading: {},
  rfiHasLoaded: {},
  rfiLastLoaded: {},
  rfiErrors: {},
};

// ============================================================================
// Helpers
// ============================================================================

/**
 * Sync updated RFI into currentRFI if the rfiId matches.
 */
function syncCurrentRFI(currentRFI: RFI | null, updated: RFI): { currentRFI: RFI | null } {
  if (currentRFI?.rfiId === updated.rfiId) {
    return { currentRFI: updated };
  }
  return { currentRFI };
}

// ============================================================================
// Slice Creator
// ============================================================================

export const createRFISlice: StateCreator<RFISlice, [], [], RFISlice> = (set, get) => ({
  ...initialRFIState,

  // ============================================================================
  // List Actions
  // ============================================================================

  /**
   * Fetch RFIs for the authenticated buyer
   */
  fetchBuyerRFIs: async (params?: { cursor?: string; status?: RFIStatus; limit?: number }) => {
    const operation = 'fetchBuyerRFIs';
    const currentState = get();

    if (isOperationLoading(currentState.rfiIsLoading, operation)) return;

    set({
      rfiIsLoading: setLoading(currentState.rfiIsLoading, operation, true),
      rfiErrors: clearOpError(currentState.rfiErrors, operation),
    });

    try {
      const response = await rfisApi.listBuyerRFIs(params);

      const state = get();
      const loadedState = setLoaded(state.rfiHasLoaded, state.rfiLastLoaded, operation, true);

      set({
        buyerRFIs: response.rfis,
        buyerRFIsNextCursor: response.nextCursor ?? null,
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiHasLoaded: loadedState.hasLoaded,
        rfiLastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      const state = get();
      const loadedState = setLoaded(state.rfiHasLoaded, state.rfiLastLoaded, operation, false);

      set({
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiErrors: setError(
          state.rfiErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to fetch buyer RFIs'
        ),
        rfiHasLoaded: loadedState.hasLoaded,
        rfiLastLoaded: loadedState.lastLoaded,
      });
    }
  },

  /**
   * GW-10607: Load the next page of buyer RFIs (cursor pagination) and
   * append it to the existing `buyerRFIs` list.
   */
  loadMoreBuyerRFIs: async () => {
    const operation = 'loadMoreBuyerRFIs';
    const currentState = get();

    if (!currentState.buyerRFIsNextCursor) return;
    if (isOperationLoading(currentState.rfiIsLoading, operation)) return;

    set({
      rfiIsLoading: setLoading(currentState.rfiIsLoading, operation, true),
      rfiErrors: clearOpError(currentState.rfiErrors, operation),
    });

    try {
      const response = await rfisApi.listBuyerRFIs({ cursor: currentState.buyerRFIsNextCursor });

      const state = get();
      set({
        buyerRFIs: [...state.buyerRFIs, ...response.rfis],
        buyerRFIsNextCursor: response.nextCursor ?? null,
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
      });
    } catch (error) {
      const state = get();
      set({
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiErrors: setError(
          state.rfiErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to load more buyer RFIs'
        ),
      });
    }
  },

  /**
   * Fetch RFIs for an organization (org admin view)
   */
  fetchOrgRFIs: async (
    orgId: string,
    params?: { cursor?: string; status?: RFIStatus; limit?: number }
  ) => {
    const operation = 'fetchOrgRFIs';
    const currentState = get();

    if (isOperationLoading(currentState.rfiIsLoading, operation)) return;

    set({
      rfiIsLoading: setLoading(currentState.rfiIsLoading, operation, true),
      rfiErrors: clearOpError(currentState.rfiErrors, operation),
    });

    try {
      const response = await rfisApi.listOrgRFIs(orgId, params);

      const state = get();
      const loadedState = setLoaded(state.rfiHasLoaded, state.rfiLastLoaded, operation, true);

      set({
        orgRFIs: response.rfis,
        orgRFIsNextCursor: response.nextCursor ?? null,
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiHasLoaded: loadedState.hasLoaded,
        rfiLastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      const state = get();
      const loadedState = setLoaded(state.rfiHasLoaded, state.rfiLastLoaded, operation, false);

      set({
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiErrors: setError(
          state.rfiErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to fetch org RFIs'
        ),
        rfiHasLoaded: loadedState.hasLoaded,
        rfiLastLoaded: loadedState.lastLoaded,
      });
    }
  },

  /**
   * Fetch RFIs assigned to the authenticated consultant
   */
  fetchConsultantRFIs: async (params?: { cursor?: string; status?: RFIStatus; limit?: number }) => {
    const operation = 'fetchConsultantRFIs';
    const currentState = get();

    if (isOperationLoading(currentState.rfiIsLoading, operation)) return;

    set({
      rfiIsLoading: setLoading(currentState.rfiIsLoading, operation, true),
      rfiErrors: clearOpError(currentState.rfiErrors, operation),
    });

    try {
      const response = await rfisApi.listConsultantRFIs(params);

      const state = get();
      const loadedState = setLoaded(state.rfiHasLoaded, state.rfiLastLoaded, operation, true);

      set({
        consultantRFIs: response.rfis,
        consultantRFIsNextCursor: response.nextCursor ?? null,
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiHasLoaded: loadedState.hasLoaded,
        rfiLastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      const state = get();
      const loadedState = setLoaded(state.rfiHasLoaded, state.rfiLastLoaded, operation, false);

      set({
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiErrors: setError(
          state.rfiErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to fetch consultant RFIs'
        ),
        rfiHasLoaded: loadedState.hasLoaded,
        rfiLastLoaded: loadedState.lastLoaded,
      });
    }
  },

  /**
   * GW-10607: Load the next page of consultant (assigned) RFIs (cursor
   * pagination) and append it to the existing `consultantRFIs` list.
   */
  loadMoreConsultantRFIs: async () => {
    const operation = 'loadMoreConsultantRFIs';
    const currentState = get();

    if (!currentState.consultantRFIsNextCursor) return;
    if (isOperationLoading(currentState.rfiIsLoading, operation)) return;

    set({
      rfiIsLoading: setLoading(currentState.rfiIsLoading, operation, true),
      rfiErrors: clearOpError(currentState.rfiErrors, operation),
    });

    try {
      const response = await rfisApi.listConsultantRFIs({
        cursor: currentState.consultantRFIsNextCursor,
      });

      const state = get();
      set({
        consultantRFIs: [...state.consultantRFIs, ...response.rfis],
        consultantRFIsNextCursor: response.nextCursor ?? null,
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
      });
    } catch (error) {
      const state = get();
      set({
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiErrors: setError(
          state.rfiErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to load more consultant RFIs'
        ),
      });
    }
  },

  /**
   * Fetch a single RFI by ID and set as currentRFI
   */
  fetchRFI: async (rfiId: string) => {
    const operation = 'fetchRFI';
    const currentState = get();

    if (isOperationLoading(currentState.rfiIsLoading, operation)) return;

    set({
      rfiIsLoading: setLoading(currentState.rfiIsLoading, operation, true),
      rfiErrors: clearOpError(currentState.rfiErrors, operation),
    });

    try {
      const rfi = await rfisApi.getRFI(rfiId);

      const state = get();
      const loadedState = setLoaded(state.rfiHasLoaded, state.rfiLastLoaded, operation, true);

      set({
        currentRFI: rfi,
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiHasLoaded: loadedState.hasLoaded,
        rfiLastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      const state = get();
      const loadedState = setLoaded(state.rfiHasLoaded, state.rfiLastLoaded, operation, false);

      set({
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiErrors: setError(
          state.rfiErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to fetch RFI'
        ),
        rfiHasLoaded: loadedState.hasLoaded,
        rfiLastLoaded: loadedState.lastLoaded,
      });
    }
  },

  // ============================================================================
  // Mutation Actions
  // ============================================================================

  /**
   * Submit a new RFI as a buyer
   */
  submitRFI: async (data: SubmitRFIRequest): Promise<RFI> => {
    const operation = 'submitRFI';
    const currentState = get();

    set({
      rfiIsLoading: setLoading(currentState.rfiIsLoading, operation, true),
      rfiErrors: clearOpError(currentState.rfiErrors, operation),
    });

    try {
      const rfi = await rfisApi.submitRFI(data);

      const state = get();
      set({
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
      });

      return rfi;
    } catch (error) {
      const state = get();
      set({
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiErrors: setError(
          state.rfiErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to submit RFI'
        ),
      });
      throw error;
    }
  },

  /**
   * Assign a consultant to an RFI
   */
  assignConsultant: async (rfiId: string, consultantUserId: string) => {
    const operation = 'assignConsultant';
    const currentState = get();

    set({
      rfiIsLoading: setLoading(currentState.rfiIsLoading, operation, true),
      rfiErrors: clearOpError(currentState.rfiErrors, operation),
    });

    try {
      const updated = await rfisApi.assignConsultant(rfiId, consultantUserId);

      const state = get();
      set({
        ...syncCurrentRFI(state.currentRFI, updated),
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
      });
    } catch (error) {
      const state = get();
      set({
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiErrors: setError(
          state.rfiErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to assign consultant'
        ),
      });
      throw error;
    }
  },

  /**
   * Cancel an RFI
   */
  cancelRFI: async (rfiId: string) => {
    const operation = 'cancelRFI';
    const currentState = get();

    set({
      rfiIsLoading: setLoading(currentState.rfiIsLoading, operation, true),
      rfiErrors: clearOpError(currentState.rfiErrors, operation),
    });

    try {
      const updated = await rfisApi.cancelRFI(rfiId);

      const state = get();
      set({
        ...syncCurrentRFI(state.currentRFI, updated),
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
      });
    } catch (error) {
      const state = get();
      set({
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiErrors: setError(
          state.rfiErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to cancel RFI'
        ),
      });
      throw error;
    }
  },

  /**
   * Mark an RFI as delivered
   */
  deliverRFI: async (rfiId: string, deliverableReference: string) => {
    const operation = 'deliverRFI';
    const currentState = get();

    set({
      rfiIsLoading: setLoading(currentState.rfiIsLoading, operation, true),
      rfiErrors: clearOpError(currentState.rfiErrors, operation),
    });

    try {
      const updated = await rfisApi.deliverRFI(rfiId, deliverableReference);

      const state = get();
      set({
        ...syncCurrentRFI(state.currentRFI, updated),
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
      });
    } catch (error) {
      const state = get();
      set({
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiErrors: setError(
          state.rfiErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to deliver RFI'
        ),
      });
      throw error;
    }
  },

  /**
   * Accept the deliverable and complete an RFI
   */
  acceptRFI: async (rfiId: string) => {
    const operation = 'acceptRFI';
    const currentState = get();

    set({
      rfiIsLoading: setLoading(currentState.rfiIsLoading, operation, true),
      rfiErrors: clearOpError(currentState.rfiErrors, operation),
    });

    try {
      const updated = await rfisApi.acceptRFI(rfiId);

      const state = get();
      set({
        ...syncCurrentRFI(state.currentRFI, updated),
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
      });
    } catch (error) {
      const state = get();
      set({
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiErrors: setError(
          state.rfiErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to accept RFI'
        ),
      });
      throw error;
    }
  },

  /**
   * Raise a dispute on an RFI
   */
  disputeRFI: async (rfiId: string, reason: string) => {
    const operation = 'disputeRFI';
    const currentState = get();

    set({
      rfiIsLoading: setLoading(currentState.rfiIsLoading, operation, true),
      rfiErrors: clearOpError(currentState.rfiErrors, operation),
    });

    try {
      const updated = await rfisApi.disputeRFI(rfiId, reason);

      const state = get();
      set({
        ...syncCurrentRFI(state.currentRFI, updated),
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
      });
    } catch (error) {
      const state = get();
      set({
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiErrors: setError(
          state.rfiErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to dispute RFI'
        ),
      });
      throw error;
    }
  },

  /**
   * Resolve a dispute on an RFI (admin only)
   */
  resolveDispute: async (rfiId: string, resolution: 'completed' | 'refunded', notes?: string) => {
    const operation = 'resolveDispute';
    const currentState = get();

    set({
      rfiIsLoading: setLoading(currentState.rfiIsLoading, operation, true),
      rfiErrors: clearOpError(currentState.rfiErrors, operation),
    });

    try {
      const updated = await rfisApi.resolveDispute(rfiId, resolution, notes);

      const state = get();
      set({
        ...syncCurrentRFI(state.currentRFI, updated),
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
      });
    } catch (error) {
      const state = get();
      set({
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiErrors: setError(
          state.rfiErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to resolve dispute'
        ),
      });
      throw error;
    }
  },

  /**
   * Propose an LTIOV extension for an RFI
   */
  extendLTIOV: async (rfiId: string, proposedLtiov: string, reason: string) => {
    const operation = 'extendLTIOV';
    const currentState = get();

    set({
      rfiIsLoading: setLoading(currentState.rfiIsLoading, operation, true),
      rfiErrors: clearOpError(currentState.rfiErrors, operation),
    });

    try {
      const updated = await rfisApi.extendLTIOV(rfiId, proposedLtiov, reason);

      const state = get();
      set({
        ...syncCurrentRFI(state.currentRFI, updated),
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
      });
    } catch (error) {
      const state = get();
      set({
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiErrors: setError(
          state.rfiErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to extend LTIOV'
        ),
      });
      throw error;
    }
  },

  /**
   * Approve a proposed LTIOV extension
   */
  approveExtension: async (rfiId: string) => {
    const operation = 'approveExtension';
    const currentState = get();

    set({
      rfiIsLoading: setLoading(currentState.rfiIsLoading, operation, true),
      rfiErrors: clearOpError(currentState.rfiErrors, operation),
    });

    try {
      const updated = await rfisApi.approveExtension(rfiId);

      const state = get();
      set({
        ...syncCurrentRFI(state.currentRFI, updated),
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
      });
    } catch (error) {
      const state = get();
      set({
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiErrors: setError(
          state.rfiErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to approve extension'
        ),
      });
      throw error;
    }
  },

  /**
   * Reject a proposed LTIOV extension
   */
  rejectExtension: async (rfiId: string) => {
    const operation = 'rejectExtension';
    const currentState = get();

    set({
      rfiIsLoading: setLoading(currentState.rfiIsLoading, operation, true),
      rfiErrors: clearOpError(currentState.rfiErrors, operation),
    });

    try {
      const updated = await rfisApi.rejectExtension(rfiId);

      const state = get();
      set({
        ...syncCurrentRFI(state.currentRFI, updated),
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
      });
    } catch (error) {
      const state = get();
      set({
        rfiIsLoading: setLoading(state.rfiIsLoading, operation, false),
        rfiErrors: setError(
          state.rfiErrors,
          operation,
          error instanceof Error ? error.message : 'Failed to reject extension'
        ),
      });
      throw error;
    }
  },

  // ============================================================================
  // Utility Actions
  // ============================================================================

  /**
   * Directly set the current RFI (for navigation or pre-loading)
   */
  setCurrentRFI: (rfi: RFI | null) => {
    set({ currentRFI: rfi });
  },

  /**
   * Clear error for a specific operation or all errors
   */
  clearRFIError: (operation?: string) => {
    const currentState = get();
    if (operation) {
      set({ rfiErrors: clearOpError(currentState.rfiErrors, operation) });
    } else {
      set({ rfiErrors: {} });
    }
  },

  /**
   * Reset RFI state to initial values
   */
  resetRFI: () => {
    set(initialRFIState);
  },
});
